import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Download, Fingerprint, KeyRound, Atom } from "lucide-react";

const LINES = [
  { text: "Own every key.", cls: "text-slate-100" },
  { text: "Command every chain.", cls: "gold-text" },
  { text: "Welcome to Cyrion.", cls: "text-slate-100" },
];

const Particles = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const pts = Array.from({ length: 55 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.6 + 0.4,
      vx: (Math.random() - 0.5) * 0.0006,
      vy: (Math.random() - 0.5) * 0.0006,
      gold: Math.random() > 0.72,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of pts) {
        p.x = (p.x + p.vx + 1) % 1;
        p.y = (p.y + p.vy + 1) % 1;
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.gold ? "rgba(212,175,55,0.55)" : "rgba(148,163,184,0.28)";
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

const TAGS = [
  { icon: KeyRound, label: "Non-Custodial" },
  { icon: Fingerprint, label: "Zero-Knowledge" },
  { icon: Atom, label: "Quantum-Safe" },
];

export const Hero = ({ onDownload, available }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateX = useTransform(sy, [0, 1], [10, -10]);
  const rotateY = useTransform(sx, [0, 1], [-12, 12]);
  const logoY = useTransform(sy, [0, 1], [14, -14]);

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <section
      id="top"
      onMouseMove={onMouseMove}
      className="relative overflow-hidden pt-36 pb-24 px-4 sm:px-6 lg:px-8 grain"
      data-testid="hero-section"
    >
      <Particles />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-gold/[0.07] blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[400px] rounded-full bg-mint/[0.05] blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="font-mono text-[11px] tracking-[0.3em] uppercase text-mint mb-8"
            data-testid="hero-eyebrow"
          >
            The sovereign crypto wallet — Windows build live
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.45 + i * 0.14 }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-7 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl"
            data-testid="hero-subcopy"
          >
            Cyrion seals your assets inside an offline-grade vault, swaps across chains atomically,
            and never phones home. One desktop app. Total command.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              onClick={onDownload}
              data-testid="hero-download-btn"
              className="group flex items-center gap-3 bg-gold text-ink font-display font-bold text-sm px-7 py-4 rounded-full hover:bg-gold-soft hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] hover:-translate-y-0.5 transition-all duration-300"
            >
              <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-300" />
              {available ? "Download for Windows" : "Windows build — soon"}
            </button>
            <div className="flex flex-wrap gap-2" data-testid="hero-trust-tags">
              {TAGS.map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.16em] uppercase text-slate-400 border border-white/10 rounded-full px-3 py-1.5 hover:border-gold/40 hover:text-gold-soft transition-colors duration-300">
                  <Icon className="w-3 h-3 text-mint" />
                  {label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.7 }}
          className="relative hidden lg:flex items-center justify-center"
          style={{ perspective: 1200 }}
          data-testid="hero-visual"
        >
          <motion.div
            style={{ rotateX, rotateY }}
            className="relative w-[380px] h-[380px] flex items-center justify-center"
          >
            <div className="absolute inset-0 rounded-full ring-conic animate-spin-slow opacity-70" style={{ maskImage: "radial-gradient(closest-side, transparent 78%, black 79%)", WebkitMaskImage: "radial-gradient(closest-side, transparent 78%, black 79%)" }} />
            <div className="absolute inset-6 rounded-full border border-white/[0.06]" />
            <div className="absolute inset-14 rounded-full border border-gold/10 animate-pulse-glow" />
            <motion.div
              style={{ y: logoY }}
              className="relative w-56 h-56 rounded-3xl overflow-hidden ring-1 ring-gold/30 shadow-[0_0_80px_rgba(212,175,55,0.22)] bg-obsidian"
            >
              <img src="/assets/cyrion-logo.png" alt="Cyrion vault emblem" className="w-full h-full object-cover" data-testid="hero-logo-img" />
            </motion.div>
            <div className="absolute -bottom-6 font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">
              Vault Core · v1 Secured
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
